import React, { CSSProperties } from 'react';
import { Point, clamp } from '../coreui';
import { LanguageKeys, Settings } from '../../CommonTypes';
import { localization } from '../../localization';
import { FAButton } from '../coreui/basic';
import { BaseStyled } from './Base.styles';

export const getMousePoint = (mouseRef: MouseEvent | Point, targetRef: HTMLElement | SVGElement) => {
  const e = mouseRef as MouseEvent;
  const rect = targetRef.getBoundingClientRect();
  const scale = targetRef.clientWidth / rect.width;
  const x = clamp(((e.clientX || mouseRef.x) - rect.left) * scale, 0, targetRef.clientWidth);
  const y = clamp(((e.clientY || mouseRef.y) - rect.top) * scale, 0, targetRef.clientHeight);
  return { x, y };
};

export interface ITool {
  getCSVEntries: () => string[][] | null;
}

export interface IMeasureToolProps {
  // data props
  index: number;
  unit: string;
  onUnitChanged?: (newUnit: string) => void;
  lengthScale: number;
  onLengthScaleChanged?: (newScale: number) => void;

  settings: Settings;

  // portals
  UIPortals: {
    portal: HTMLDivElement | null;
  }[];
  RenderPortals: {
    portal: SVGSVGElement | null;
    overlayScale: number;
    hideText?: boolean;
    requestStyle?: (style: CSSProperties) => void;
  }[];

  // interaction props
  allowInteraction: boolean;
  onChangeInteracting?: (interacting: boolean) => void;
  onFinish?: (data: Point[]) => void;
  onDelete?: () => void;
};

export interface IMTInternalProps {
  name?: string;
  onUpdateName?: (newName: string) => void;
  data: Point[];
  onUpdateData?: (newData: Point[] | null) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  showTooltip?: Point;
  hidden: number;
  onHiddenChange: (newHidden: number) => void;
}

export interface IMTStateProps {
  highlight?: boolean;
  grabbing: boolean;
};

export const dataEqual = (d1: Point[], d2: Point[]) => {
  if (d1.length !== d2.length)
    return false;
  return !d1.some((p1, i) => p1 !== d2[i]);
};

export const getDefaultName = (index: number, lang: LanguageKeys) => `${localization[lang].measurements.defaultName} ${index + 1}`;


export const MeasureToolUIBase: React.FC<IMeasureToolProps & IMTInternalProps & IMTStateProps> = ({ name, index, allowInteraction, highlight, grabbing, settings, onUpdateName, onDelete, onMouseEnter, onMouseLeave, children }) => {
  const highlightStyle: CSSProperties = (highlight && allowInteraction) ? { backgroundColor: '#156EAC40' } : {};
  const interactStyle: CSSProperties = (allowInteraction && !grabbing) ? {} : { pointerEvents: 'none' };
  const mName = name || getDefaultName(index, settings.lang);

  return <BaseStyled
    onMouseEnter={allowInteraction ? onMouseEnter : undefined}
    onMouseMove={allowInteraction ? onMouseEnter : undefined}
    onMouseLeave={allowInteraction ? onMouseLeave : undefined}
    style={{ ...highlightStyle, ...interactStyle}}>
    <span>{`${index + 1}`}</span>
    <input value={mName} disabled={!onUpdateName} onChange={onUpdateName ? e => onUpdateName(e.currentTarget.value) : undefined} />
    <FAButton icon='trash-alt' onClick={onDelete} size={25} data-tip={ localization[settings.lang].tooltips.deleteSingleMeasurement(mName) }/>
    <div>
      {children}
    </div>
  </BaseStyled>
}; 
